import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthMe, AuthUser, Session, UserRole } from '../types';
import { api } from '../api/client';

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  me: AuthMe | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<Session>;
  completeGoogleSession: (
    code: string
  ) => Promise<Session & { returnTo?: string; lang?: string; needsOnboarding?: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
  markOnboardingComplete: () => void;
  hasRole: (role: UserRole) => boolean;
  isAdmin: boolean;
  switchOrganization: (organizationId: string) => Promise<AuthMe>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Legacy key — cleared on boot; tokens must not live in localStorage. */
const LEGACY_SESSION_KEY = 'becoder_session_v2';
const PROFILE_CACHE_KEY = 'becoder_profile_v1';

function loadProfileCache(): Session | null {
  try {
    localStorage.removeItem(LEGACY_SESSION_KEY);
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // Strip any accidentally cached token
    return {
      user: parsed.user,
      expiresAt: parsed.expiresAt,
      needsOnboarding: parsed.needsOnboarding,
      activeOrganizationId: parsed.activeOrganizationId,
    };
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<AuthMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const persist = useCallback((next: Session | null, nextMe?: AuthMe | null) => {
    setSession(next);
    if (nextMe !== undefined) setMe(nextMe);
    if (next) {
      localStorage.setItem(
        PROFILE_CACHE_KEY,
        JSON.stringify({
          user: next.user,
          expiresAt: next.expiresAt,
          needsOnboarding: next.needsOnboarding,
          activeOrganizationId: next.activeOrganizationId,
        })
      );
      // Memory-only bearer for transition; cookie is authoritative.
      if (next.accessToken) api.setToken(next.accessToken);
    } else {
      localStorage.removeItem(PROFILE_CACHE_KEY);
      localStorage.removeItem(LEGACY_SESSION_KEY);
      api.setToken(null);
      setMe(null);
    }
  }, []);

  useEffect(() => {
    const cached = loadProfileCache();
    api
      .me()
      .then((authMe) => {
        persist(
          {
            user: authMe.user,
            expiresAt: authMe.session?.expiresAt || new Date(Date.now() + 12 * 3600_000).toISOString(),
            needsOnboarding: authMe.onboarding.needsOnboarding,
            activeOrganizationId: authMe.activeOrganizationId,
          },
          authMe
        );
      })
      .catch(() => {
        if (cached) persist(null);
        else persist(null);
      })
      .finally(() => setIsLoading(false));
  }, [persist]);

  const login = useCallback(
    async (email: string, password: string) => {
      const next = await api.login(email, password);
      const authMe = await api.me().catch(() => null);
      persist(
        {
          user: next.user,
          accessToken: next.accessToken,
          expiresAt: next.expiresAt,
          needsOnboarding: next.needsOnboarding ?? next.user.needsOnboarding,
          activeOrganizationId: authMe?.activeOrganizationId,
        },
        authMe
      );
      return next;
    },
    [persist]
  );

  const completeGoogleSession = useCallback(
    async (code: string) => {
      const next = await api.completeGoogleSignIn(code);
      const authMe = await api.me().catch(() => null);
      persist(
        {
          user: next.user,
          accessToken: next.accessToken,
          expiresAt: next.expiresAt,
          needsOnboarding: next.needsOnboarding ?? next.user.needsOnboarding,
          activeOrganizationId: authMe?.activeOrganizationId,
        },
        authMe
      );
      return next;
    },
    [persist]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    persist(null);
  }, [persist]);

  const refreshUser = useCallback(async () => {
    const authMe = await api.me();
    persist(
      {
        user: authMe.user,
        expiresAt: authMe.session?.expiresAt || session?.expiresAt || '',
        needsOnboarding: authMe.onboarding.needsOnboarding,
        activeOrganizationId: authMe.activeOrganizationId,
        accessToken: session?.accessToken,
      },
      authMe
    );
    return authMe.user;
  }, [persist, session]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      const authMe = await api.switchOrganization(organizationId);
      persist(
        {
          user: authMe.user,
          expiresAt: authMe.session?.expiresAt || session?.expiresAt || '',
          needsOnboarding: authMe.onboarding.needsOnboarding,
          activeOrganizationId: authMe.activeOrganizationId,
          accessToken: session?.accessToken,
        },
        authMe
      );
      return authMe;
    },
    [persist, session]
  );

  const markOnboardingComplete = useCallback(() => {
    if (!session) return;
    persist(
      {
        ...session,
        needsOnboarding: false,
        user: { ...session.user, needsOnboarding: false },
      },
      me
        ? {
            ...me,
            user: { ...me.user, needsOnboarding: false },
            onboarding: { ...me.onboarding, needsOnboarding: false },
          }
        : me
    );
  }, [persist, session, me]);

  const hasRole = useCallback((role: UserRole) => session?.user.role === role, [session]);

  const value = useMemo(
    () => ({
      user: session?.user ?? null,
      session,
      me,
      isAuthenticated: Boolean(session),
      isLoading,
      login,
      completeGoogleSession,
      logout,
      refreshUser,
      markOnboardingComplete,
      hasRole,
      isAdmin: session?.user.role === 'platform_admin',
      switchOrganization,
    }),
    [
      session,
      me,
      isLoading,
      login,
      completeGoogleSession,
      logout,
      refreshUser,
      markOnboardingComplete,
      hasRole,
      switchOrganization,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
